import { useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import ModalCadastrarServico from './ModalCadastrarServico';
import type { EstadoCarga } from './cargaAuxiliar';

// Modal compacto: lista de serviços com busca por digitação. Confirma com Enter ou clique.
// `servicos` vazio só quer dizer "todos já têm grupo" quando `estado` é 'ok':
// se a lista não veio, o modal diz isso e oferece "Tentar de novo".
export default function ModalSelecionarServico({ servicos, estado, onTentarDeNovo, onClose, onSelecionar, onCriado }: {
  servicos: any[];
  estado: EstadoCarga;
  onTentarDeNovo: () => void;
  onClose: () => void;
  onSelecionar: (servicoId: string) => void;
  onCriado?: (novo: any) => void;  // chamado quando usuário cria serviço novo daqui
}) {
  const [busca, setBusca] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [modalCadastro, setModalCadastro] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus(); }, []);

  const filtrados = servicos.filter(s => {
    if (!busca.trim()) return true;
    const q = busca.toLowerCase();
    return (
      (s.nome || '').toLowerCase().includes(q) ||
      (s.codigo || '').toLowerCase().includes(q) ||
      (s.area || '').toLowerCase().includes(q) ||
      (s.descricao || '').toLowerCase().includes(q)
    );
  });

  useEffect(() => { setHighlight(0); }, [busca]);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, Math.max(filtrados.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const escolhido = filtrados[highlight];
      if (escolhido) onSelecionar(escolhido.id);
      // Enter sem resultado abre modal pré-preenchido — só com a lista na mão:
      // sem ela, "sem resultado" não prova que o serviço não existe (viraria duplicata).
      else if (busca.trim() && estado === 'ok') setModalCadastro(true);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  return (
    <>
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2500,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 10, padding: 16, width: '94%', maxWidth: 520,
          maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <strong style={{ fontSize: 14, color: '#020D7F', flex: 1 }}>📦 Selecionar serviço para novo grupo</strong>
          <button onClick={onClose} style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer', color: '#888' }}>×</button>
        </div>
        <input ref={inputRef} value={busca} onChange={e => setBusca(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="🔎 Pesquisar por nome, código ou área..."
          style={{
            width: '100%', padding: '8px 12px', fontSize: 13, borderRadius: 6,
            border: '1px solid #ddd', boxSizing: 'border-box', marginBottom: 10,
          }} />
        {estado === 'erro' ? (
          <Alert type="error" showIcon title="Não consegui carregar a lista de serviços."
            description="Isto não quer dizer que não há serviço disponível — só que a lista não veio."
            action={<Button size="small" icon={<ReloadOutlined />} onClick={onTentarDeNovo}>Tentar de novo</Button>} />
        ) : estado === 'carregando' ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 12 }}>Carregando serviços...</div>
        ) : servicos.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#aaa', fontSize: 12 }}>
            Todos os serviços cadastrados já têm grupo nesta composição.<br/>
            Cadastre mais em <strong>Comercial → Serviços/Produtos</strong>.
          </div>
        ) : filtrados.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#666', fontSize: 12 }}>
            Nenhum serviço corresponde a <strong>"{busca}"</strong>.
            <div style={{ marginTop: 12 }}>
              <button onClick={() => setModalCadastro(true)}
                style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600,
                  border: 'none', borderRadius: 5, background: '#0930D0', color: '#fff', cursor: 'pointer' }}>
                + Cadastrar "{busca}" como novo serviço
              </button>
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: 'auto', border: '0.5px solid #eee', borderRadius: 6 }}>
            {filtrados.map((s, i) => (
              <div key={s.id}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => onSelecionar(s.id)}
                style={{
                  padding: '8px 12px', cursor: 'pointer',
                  background: i === highlight ? '#E7ECFD' : '#fff',
                  borderBottom: '0.5px solid #f5f5f5',
                  display: 'flex', flexDirection: 'column', gap: 2,
                }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: i === highlight ? '#0930D0' : '#222' }}>
                  {s.codigo ? <span style={{ color: '#888', fontWeight: 400, marginRight: 6 }}>[{s.codigo}]</span> : null}
                  {s.nome}
                </div>
                {(s.area || s.descricao) && (
                  <div style={{ fontSize: 10, color: '#888' }}>
                    {s.area && <span>{s.area}</span>}
                    {s.area && s.descricao && <span> · </span>}
                    {s.descricao && <span style={{ fontStyle: 'italic' }}>{s.descricao}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <button onClick={() => setModalCadastro(true)} disabled={estado !== 'ok'}
            title={estado === 'ok' ? undefined : 'Disponível quando a lista de serviços carregar — sem ela não dá para ver se o serviço já existe.'}
            style={{ fontSize: 11, padding: '4px 10px', borderRadius: 4,
              border: '0.5px solid #0930D0', background: '#fff', color: '#0930D0', fontWeight: 600,
              cursor: estado === 'ok' ? 'pointer' : 'not-allowed', opacity: estado === 'ok' ? 1 : 0.5 }}>
            + Cadastrar novo serviço
          </button>
          <span style={{ fontSize: 10, color: '#888' }}>
            ↑↓ navegar · Enter selecionar · Esc fechar
          </span>
        </div>
      </div>
    </div>
    {modalCadastro && (
      <ModalCadastrarServico
        nomeInicial={busca}
        onClose={() => setModalCadastro(false)}
        onCriado={(novo) => {
          setModalCadastro(false);
          if (onCriado) onCriado(novo);
          onSelecionar(novo.id);
        }}
      />
    )}
    </>
  );
}
